import React from 'react';
import { Send, MessageSquare, MessagesSquare } from 'lucide-react';
import { supabase } from '../lib/supabase';
import type { Message } from '../types';
import { AnonymousIcon } from './AnonymousIcon';

interface LiveChatProps {
  postId: string;
}

export function LiveChat({ postId }: LiveChatProps) {
  const [messages, setMessages] = React.useState<Message[]>([]);
  const [newMessage, setNewMessage] = React.useState('');
  const chatRef = React.useRef<HTMLDivElement>(null);
  const inputRef = React.useRef<HTMLInputElement>(null);
  const [isLoading, setIsLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const channelRef = React.useRef<ReturnType<typeof supabase.channel> | null>(null);
  const [isSending, setIsSending] = React.useState(false);
  const formRef = React.useRef<HTMLFormElement>(null);

  // Scroll to bottom when new messages arrive
  const scrollToBottom = React.useCallback((force = false) => {
    if (chatRef.current) {
      const scrollOptions = {
        top: chatRef.current.scrollHeight
      };
      
      if (force) {
        chatRef.current.scrollTo(scrollOptions);
      } else {
        chatRef.current.scrollTo({ ...scrollOptions, behavior: 'smooth' });
      }
    }
  }, []);

  React.useEffect(() => {
    setIsLoading(true);
    setError(null);

    // Subscribe to new messages
    channelRef.current = supabase
      .channel(`room:${postId}`)
      .on('broadcast', { event: 'message' }, ({ payload }) => {
        setMessages(prev => [...prev, payload]);
        scrollToBottom();
      })
      .subscribe();

    // Load existing messages
    supabase
      .from('messages')
      .select('*')
      .eq('post_id', postId)
      .order('created_at', { ascending: true })
      .limit(100)
      .then(({ data, error: err }) => {
        if (err) {
          setError('Failed to load messages');
          console.error('Error loading messages:', err.message);
        } else {
          setMessages(data as Message[] || []);          
          // Force scroll to bottom after a small delay to ensure content is rendered
          setTimeout(() => scrollToBottom(true), 100);
        }
        setIsLoading(false);
      });

    return () => {
      if (channelRef.current) {
        supabase.removeChannel(channelRef.current);
      }
    };
  }, [postId, scrollToBottom]);

  const sendMessage = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newMessage.trim() || isSending) return;
    
    setIsSending(true);
    setError(null);
    
    const randomUsername = `user_${Math.floor(Math.random() * 10000)}`;
    const message = {
      id: crypto.randomUUID(),
      username: randomUsername,
      content: newMessage.trim(),
      user_avatar: '', // We'll use the AnonymousIcon instead
      created_at: new Date().toISOString(),
      post_id: postId,
    };

    try {
      const { error: insertError } = await supabase
        .from('messages')
        .insert([message]);

      if (insertError) throw insertError;
      
      const { error: broadcastError } = await supabase
        .channel(`room:${postId}`)
        .send({
          type: 'broadcast',
          event: 'message',
          payload: message,
        });

      if (broadcastError) throw broadcastError;

      setNewMessage('');
      scrollToBottom();
    } catch (err) {
      console.error('Error sending message:', err);
      setError('Failed to send message. Please try again.');
    } finally {
      setIsSending(false);
    }
  };

  const handleFocus = () => {
    // Prevent scrolling when input is focused
    if (chatRef.current) {
      chatRef.current.style.overflow = 'hidden';
    }
    // Small delay to ensure keyboard is shown
    setTimeout(() => {
      if (formRef.current) {
        formRef.current.scrollIntoView({ behavior: 'smooth' });
      }
      // Re-enable scrolling
      if (chatRef.current) {
        chatRef.current.style.overflow = 'auto';
      }
    }, 100);
  };

  const handleBlur = () => {
    if (chatRef.current) {
      chatRef.current.style.overflow = 'auto';
    }
  };

  return (
    <div className="flex flex-col flex-1 overflow-hidden">
      <div
        ref={chatRef}
        className="flex-1 overflow-y-auto p-4 space-y-4 overscroll-contain custom-scrollbar pb-safe"
        style={{ scrollBehavior: 'smooth' }}
      >
        {isLoading ? (
          <div className="flex justify-center items-center h-full">
            <div className="animate-spin text-gray-400">
              <MessageSquare className="w-8 h-8" />
            </div>
          </div>
        ) : error ? (
          <div className="flex flex-col items-center justify-center h-full text-red-400">
            <MessageSquare className="w-12 h-12 mb-4" />
            <p className="text-center">{error}</p>
            <button 
              onClick={() => window.location.reload()} 
              className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-full"
            >
              Retry
            </button>
          </div>
        ) : messages.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full text-gray-400">
            <MessagesSquare className="w-12 h-12 mb-4" />
            <p className="text-center mb-2">No messages yet. Start the conversation!</p>
            <p className="text-sm text-gray-500 text-center">
              Type your message below to chat anonymously
            </p>
          </div>
        ) : (
          messages.map((message) => (
            <div key={message.id} className="flex items-start gap-2 animate-[fadeIn_0.2s_ease-out]">
              <AnonymousIcon seed={message.username} className="w-8 h-8 shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="text-gray-400 text-sm font-medium">
                  {message.username}
                </p>
                <p className="text-white break-words">{message.content}</p>
              </div>
            </div>
          ))
        )}
      </div>
      
      <form 
        onSubmit={sendMessage}
        ref={formRef}
        className="flex-shrink-0 p-4 bg-black/90 backdrop-blur-sm border-t border-gray-800 pb-safe"
      >
        <div className="flex items-center gap-2 max-w-2xl mx-auto">
          <input
            ref={inputRef}
            type="text"
            enterKeyHint="send"
            value={newMessage}
            onChange={(e) => setNewMessage(e.target.value)}
            onFocus={handleFocus}
            onBlur={handleBlur}
            placeholder="Send a message anonymously..."
            className="flex-1 bg-gray-800/80 rounded-full px-4 py-2 text-white outline-none focus:ring-2 focus:ring-blue-500 placeholder-gray-400 min-h-[44px] appearance-none"
            maxLength={500}
          />
          <button
            type="submit"
            disabled={!newMessage.trim() || isSending}
            className="bg-blue-600 p-2 rounded-full disabled:opacity-50 disabled:cursor-not-allowed hover:bg-blue-500 transition-colors min-h-[40px] min-w-[40px] flex items-center justify-center"
          >
            {isSending ? (
              <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
            ) : (
              <Send className="w-5 h-5 text-white" />
            )}
          </button>
        </div>
        {error && (
          <p className="text-red-400 text-sm mt-2 text-center">
            {error}
          </p>
        )}
      </form>
    </div>
  );
}